import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Modal, notification, Tag } from 'antd';
import {
  disableTwoFactorApi,
  enableTwoFactorApi,
  errorCodeOf,
  setupTwoFactorApi,
  TwoFactorSetup,
  twoFactorErrorMessage,
} from 'api-calls/two-factor';
import BackupCodes from 'apps/home/components/two-factor/BackupCodes';
import TwoFactorCodeInput from 'apps/home/components/two-factor/TwoFactorCodeInput';
import TwoFactorSetupForm from 'apps/home/components/two-factor/TwoFactorSetupForm';
import { Button } from 'common/ui/button';
import { ADMIN_ACCOUNT_QUERY_KEY, useAdminAccount } from 'hooks/use-admin-account';
import { useState } from 'react';
import { Link } from 'react-router-dom';

// Below this many unused backup codes, suggest generating new ones (turning
// 2FA off and on again, or asking a superadmin to reset it).
const LOW_BACKUP_CODES = 3;

export default function TwoFactor() {
  const queryClient = useQueryClient();
  const { data: account, isLoading } = useAdminAccount();

  // Setup in progress: the new secret, then the backup codes (shown once).
  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [setupError, setSetupError] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableError, setDisableError] = useState('');

  const refreshAccount = () =>
    queryClient.invalidateQueries({ queryKey: ADMIN_ACCOUNT_QUERY_KEY });

  const { mutate: startSetup, isPending: isStarting } = useMutation({
    mutationFn: () => setupTwoFactorApi.queryFn(undefined),
    onSuccess: (response: any) => {
      setSetupError('');
      setSetup(response?.data || null);
    },
    onError: (err: any) => {
      notification.open({ type: 'error', message: twoFactorErrorMessage(err) });
    },
  });

  const { mutate: enable, isPending: isEnabling } = useMutation({
    mutationFn: (code: string) => enableTwoFactorApi.queryFn({ code }),
    onSuccess: (response: any) => {
      setSetup(null);
      setSetupError('');
      setBackupCodes(response?.data?.backup_codes || []);
      refreshAccount();
    },
    onError: (err: any) => {
      const code = errorCodeOf(err);
      if (code === 'two_factor_setup_not_started' || code === 'two_factor_already_enabled') {
        setSetup(null);
        refreshAccount();
        notification.open({ type: 'error', message: twoFactorErrorMessage(err) });
        return;
      }
      setSetupError(twoFactorErrorMessage(err));
    },
  });

  const { mutate: disable, isPending: isDisabling } = useMutation({
    mutationFn: (code: string) => disableTwoFactorApi.queryFn({ code }),
    onSuccess: () => {
      setDisableCode('');
      setDisableError('');
      refreshAccount();
      notification.open({
        type: 'success',
        message: 'Đã tắt xác thực hai lớp',
      });
    },
    onError: (err: any) => {
      if (errorCodeOf(err) === 'two_factor_required') {
        refreshAccount();
      }
      setDisableError(twoFactorErrorMessage(err));
    },
  });

  const handleDisable = (e: React.FormEvent) => {
    e.preventDefault();
    const code = disableCode.trim();
    if (!code) return;
    setDisableError('');
    Modal.confirm({
      title: 'Tắt xác thực hai lớp?',
      content:
        'Đăng nhập sẽ quay lại dùng mã gửi qua email. Các mã dự phòng hiện tại sẽ không dùng được nữa.',
      okText: 'Tắt',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: () => disable(code),
    });
  };

  const renderBody = () => {
    if (isLoading || !account) {
      return <div className="text-center text-gray-500">Đang tải...</div>;
    }

    // Shown once, right after turning 2FA on; takes precedence over the
    // (already refreshed) account state.
    if (backupCodes) {
      return (
        <div className="space-y-4">
          <Alert type="success" showIcon message="Đã bật xác thực hai lớp" />
          <BackupCodes codes={backupCodes} onDone={() => setBackupCodes(null)} />
        </div>
      );
    }

    if (setup) {
      return (
        <TwoFactorSetupForm
          setup={setup}
          onSubmit={code => {
            setSetupError('');
            enable(code);
          }}
          isPending={isEnabling}
          error={setupError}
          submitLabel="Bật xác thực hai lớp"
          onCancel={() => {
            setSetup(null);
            setSetupError('');
          }}
        />
      );
    }

    const status = (
      <div className="flex items-center justify-between">
        <span className="font-medium">Trạng thái</span>
        {account.two_factor_enabled ? (
          <Tag color="green">Đang bật</Tag>
        ) : (
          <Tag color="orange">Đang tắt</Tag>
        )}
      </div>
    );

    const requiredNote = account.two_factor_required && (
      <Alert
        type="info"
        showIcon
        message="Vai trò của bạn bắt buộc bật xác thực hai lớp."
      />
    );

    if (!account.two_factor_enabled) {
      return (
        <div className="space-y-4">
          {status}
          {requiredNote}
          <p className="text-sm text-gray-600">
            Khi bật, sau khi đăng nhập Google bạn nhập mã 6 số từ ứng dụng xác
            thực trên điện thoại, thay cho mã gửi qua email.
          </p>
          <Button
            className="w-full"
            onClick={() => startSetup()}
            disabled={isStarting}
          >
            {isStarting ? 'Đang tải...' : 'Bật xác thực hai lớp'}
          </Button>
        </div>
      );
    }

    const backupCodesLeft = account.backup_codes_left ?? 0;
    return (
      <div className="space-y-4">
        {status}
        <div className="flex items-center justify-between">
          <span className="font-medium">Mã dự phòng còn lại</span>
          <span>{backupCodesLeft}</span>
        </div>
        {backupCodesLeft <= LOW_BACKUP_CODES && (
          <Alert
            type="warning"
            showIcon
            message={
              account.two_factor_required
                ? 'Bạn sắp hết mã dự phòng. Liên hệ quản trị viên để đặt lại xác thực hai lớp nếu cần.'
                : 'Bạn sắp hết mã dự phòng. Tắt và bật lại xác thực hai lớp để nhận mã mới.'
            }
          />
        )}
        {requiredNote}
        {!account.two_factor_required && (
          <form onSubmit={handleDisable} className="space-y-3 border-t pt-4">
            <label className="block font-medium">Tắt xác thực hai lớp</label>
            <TwoFactorCodeInput
              value={disableCode}
              onChange={setDisableCode}
              allowBackupCode
              autoFocus={false}
              disabled={isDisabling}
            />
            {disableError && (
              <div className="text-red-500 text-sm">{disableError}</div>
            )}
            <Button
              type="submit"
              variant="destructive"
              className="w-full"
              disabled={isDisabling || !disableCode.trim()}
            >
              {isDisabling ? 'Đang tắt...' : 'Tắt xác thực hai lớp'}
            </Button>
          </form>
        )}
      </div>
    );
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
        <Link to="/security" className="text-sm text-gray-500">
          ← Bảo mật tài khoản
        </Link>
        <h2 className="mb-6 mt-2 text-2xl font-bold text-center">
          Xác thực hai lớp
        </h2>
        {renderBody()}
      </div>
    </div>
  );
}
